import { useState, useEffect, useMemo } from "react";
import { logStore } from "../utils/operationLogStore";
import type { LogEntry } from "../utils/operationLogStore";

const actionConfig: Record<LogEntry["actionType"], { label: string; icon: string; color: string }> = {
  view: { label: "进入查看", icon: "ri-eye-line", color: "bg-gray-100 text-gray-600" },
  impersonate: { label: "身份模拟", icon: "ri-user-shared-line", color: "bg-amber-50 text-amber-600" },
  suspend: { label: "停用租户", icon: "ri-forbid-line", color: "bg-red-50 text-red-600" },
  activate: { label: "激活租户", icon: "ri-check-double-line", color: "bg-green-50 text-green-700" },
  plan_change: { label: "套餐变更", icon: "ri-price-tag-3-line", color: "bg-sky-50 text-sky-600" },
  billing: { label: "收费操作", icon: "ri-bill-line", color: "bg-teal-50 text-teal-600" },
  login: { label: "平台登录", icon: "ri-login-box-line", color: "bg-green-50 text-green-600" },
  logout: { label: "平台退出", icon: "ri-logout-box-r-line", color: "bg-gray-100 text-gray-500" },
  create_tenant: { label: "开通租户", icon: "ri-add-box-line", color: "bg-amber-50 text-amber-700" },
  edit_settings: { label: "修改设置", icon: "ri-settings-4-line", color: "bg-gray-100 text-gray-600" },
};

const resultConfig = {
  success: { label: "成功", color: "bg-green-50 text-green-600" },
  failed: { label: "失败", color: "bg-red-50 text-red-500" },
  warning: { label: "警告", color: "bg-amber-50 text-amber-600" },
};

type FilterType = "all" | LogEntry["actionType"];
type ResultFilter = "all" | LogEntry["result"];

function logTimestampInRange(timestamp: string, range: "today" | "7d" | "30d"): boolean {
  const t = new Date(timestamp.replace(" ", "T"));
  if (Number.isNaN(t.getTime())) return true;
  const now = new Date();
  const start = new Date(now);
  if (range === "today") start.setHours(0, 0, 0, 0);
  else {
    start.setDate(start.getDate() - (range === "7d" ? 7 : 30));
    start.setHours(0, 0, 0, 0);
  }
  return t >= start;
}

export default function OperationLogs() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState<FilterType>("all");
  const [resultFilter, setResultFilter] = useState<ResultFilter>("all");
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [dateFilter, setDateFilter] = useState<"today" | "7d" | "30d">("7d");

  useEffect(() => {
    const unsub = logStore.subscribe(() => setLogs(logStore.getLogs()));
    setLoading(true);
    void logStore.refreshFromApi().finally(() => setLoading(false));
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    return logs.filter((log) => {
      if (!logTimestampInRange(log.timestamp, dateFilter)) return false;
      const matchSearch =
        !search ||
        log.targetTenant.includes(search) ||
        log.targetTenantCode.includes(search) ||
        log.detail.includes(search);
      const matchAction =
        actionFilter === "all" ||
        (actionFilter === "suspend"
          ? log.actionType === "suspend" || log.actionType === "activate"
          : log.actionType === actionFilter);
      const matchResult = resultFilter === "all" || log.result === resultFilter;
      return matchSearch && matchAction && matchResult;
    });
  }, [logs, search, actionFilter, resultFilter, dateFilter]);

  const stats = useMemo(() => ({
    total: logs.length,
    success: logs.filter((l) => l.result === "success").length,
    warning: logs.filter((l) => l.result === "warning").length,
    failed: logs.filter((l) => l.result === "failed").length,
  }), [logs]);

  const actionTypes: Array<{ id: FilterType; label: string }> = [
    { id: "all", label: "全部操作" },
    { id: "impersonate", label: "身份模拟" },
    { id: "suspend", label: "停用/激活" },
    { id: "plan_change", label: "套餐变更" },
    { id: "billing", label: "收费操作" },
    { id: "create_tenant", label: "开通租户" },
    { id: "login", label: "登录/退出" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">操作日志</h2>
          <p className="text-xs text-gray-400 mt-0.5">实时记录平台管理员对所有租户的操作行为</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-xl">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            实时追踪中
          </div>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {(["today", "7d", "30d"] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDateFilter(d)}
                className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${
                  dateFilter === d ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {d === "today" ? "今日" : d === "7d" ? "近7天" : "近30天"}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50 cursor-pointer transition-all whitespace-nowrap">
            <i className="ri-download-line"></i>
            导出日志
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: "操作总次数", value: stats.total, icon: "ri-history-line", color: "text-gray-700" },
          { label: "成功操作", value: stats.success, icon: "ri-check-double-line", color: "text-green-600" },
          { label: "系统警告", value: stats.warning, icon: "ri-alert-line", color: "text-amber-500" },
          { label: "操作失败", value: stats.failed, icon: "ri-close-circle-line", color: "text-red-500" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <i className={`${s.icon} text-base ${s.color}`}></i>
              <span className="text-xs text-gray-500">{s.label}</span>
            </div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-5">
        {/* Log List */}
        <div className="flex-1 min-w-0">
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索租户名称、操作内容..."
                className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors"
              />
            </div>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value as FilterType)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 bg-white focus:outline-none focus:border-amber-400 cursor-pointer"
            >
              {actionTypes.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value as ResultFilter)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 bg-white focus:outline-none focus:border-amber-400 cursor-pointer"
            >
              <option value="all">全部结果</option>
              <option value="success">成功</option>
              <option value="warning">警告</option>
              <option value="failed">失败</option>
            </select>
            <span className="text-xs text-gray-400 whitespace-nowrap">共 {filtered.length} 条</span>
          </div>

          {/* Timeline Log */}
          <div className="space-y-1">
            {loading && logs.length === 0 && (
              <div className="text-center py-12 text-sm text-gray-400">加载操作日志…</div>
            )}
            {filtered.map((log, index) => {
              const cfg = actionConfig[log.actionType];
              const res = resultConfig[log.result];
              const isSelected = selectedLog?.id === log.id;
              return (
                <div
                  key={log.id}
                  onClick={() => setSelectedLog(isSelected ? null : log)}
                  className={`bg-white rounded-xl px-4 py-3 cursor-pointer transition-all border-2 ${
                    isSelected ? "border-amber-400" : "border-transparent hover:border-gray-100"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center flex-shrink-0 mt-0.5">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full ${cfg.color}`}>
                        <i className={`${cfg.icon} text-xs`}></i>
                      </div>
                      {index < filtered.length - 1 && (
                        <div className="w-px h-3 bg-gray-100 mt-1"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-gray-800">{cfg.label}</span>
                          {log.targetTenantCode !== "platform" && (
                            <span className="text-xs text-gray-500">→ {log.targetTenant}</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${res.color}`}>
                            {res.label}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">{log.timestamp}</span>
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-1 leading-relaxed">{log.detail}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <i className="ri-file-search-line text-4xl text-gray-200"></i>
              </div>
              <p className="text-sm">暂无匹配的操作记录</p>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedLog && (
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl p-5 sticky top-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 text-sm">操作详情</h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium mb-4 ${actionConfig[selectedLog.actionType].color}`}>
                <i className={`${actionConfig[selectedLog.actionType].icon}`}></i>
                {actionConfig[selectedLog.actionType].label}
              </div>

              <div className="space-y-3">
                {[
                  { label: "操作时间", value: selectedLog.timestamp, icon: "ri-time-line" },
                  { label: "操作人", value: `${selectedLog.operator}（${selectedLog.operatorRole}）`, icon: "ri-user-line" },
                  { label: "目标租户", value: selectedLog.targetTenant, icon: "ri-building-4-line" },
                  { label: "租户代码", value: selectedLog.targetTenantCode, icon: "ri-hashtag" },
                  { label: "来源 IP", value: selectedLog.ip, icon: "ri-global-line" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-gray-400 text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-sm text-gray-700 font-medium mt-0.5">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-50">
                <p className="text-xs text-gray-400 mb-2">操作内容</p>
                <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-3">
                  {selectedLog.detail}
                </p>
              </div>

              <div className="mt-3">
                <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${resultConfig[selectedLog.result].color}`}>
                  <i className={`${selectedLog.result === "success" ? "ri-check-circle-line" : selectedLog.result === "warning" ? "ri-alert-line" : "ri-close-circle-line"}`}></i>
                  执行{resultConfig[selectedLog.result].label}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
